import { IssueDto, IssueStatus, IssueType, ObjectType } from '@operations/app/api'
import { IssueStore } from '@operations/stores/IssueStore'
import _ from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'

export interface IContainerJourneyData {
  id: number
  containerId?: number | null
  containerNumber?: string | null
  inboundOrderId?: number | null
  inboundVisitId?: number | null
  inboundVisitIdentifier?: string | null
  inboundRailcarTrackPositionId?: number | null
  outboundOrderId?: number | null
  outboundVisitId?: number | null
  outboundVisitIdentifier?: string | null
  outboundRailcarTrackPositionId?: number | null
}
export class IssueWithJourney implements IssueDto {
  'id': number
  'orderId'?: number | null | undefined
  'issueType': IssueType
  'issueStatus': IssueStatus
  'objectType': ObjectType
  'objectId': number
  'notes'?: string | null | undefined
  'carrierVisitIds': Array<number>

  'containerJourneyData': IContainerJourneyData | undefined

  getContainerNumber(): string | null | undefined {
    return this.containerJourneyData?.containerNumber
  }
}

export class IssueUIStore {
  issues: IssueWithJourney[] = []
  issueFilterStatus?: IssueStatus
  issueFilterType?: IssueType
  containerNumberFilter? = ''
  carrierNameFilter? = ''
  isFilterEnabled = false
  filterByVisitId?: number
  filterByRailcarTrackPositionId?: number

  constructor(private issueStore: IssueStore) {
    makeObservable(this, {
      issues: observable,
      issueFilterStatus: observable,
      issueFilterType: observable,
      containerNumberFilter: observable,
      carrierNameFilter: observable,
      isFilterEnabled: observable,
      filterByVisitId: observable,
      filterByRailcarTrackPositionId: observable,

      fetch: action,
      setContainerNumberFilter: action,
      setIssueFilterStatus: action,
      setIssueFilterType: action,
      setCarrierNameFilter: action,
      enableFilters: action,
      setFilterByVisitId: action,
      setFilterByRailCarTrackPositionId: action,

      dataFilteredByIssueStatus: computed,
      issueTypes: computed,
      filteredData: computed,
    })
  }

  async fetch() {
    if (this.issueStore.items.length) return
    await this.issueStore.load()
  }

  setContainerNumberFilter(filter?: string) {
    this.containerNumberFilter = filter
  }

  setIssueFilterStatus(issueFilterType?: IssueStatus) {
    if (this.issueFilterStatus !== issueFilterType) {
      this.issueFilterStatus = issueFilterType
    }
  }

  setIssueFilterType(issueFilterType?: string) {
    if (this.issueFilterType !== issueFilterType) {
      this.issueFilterType = issueFilterType as IssueType
    }
  }

  setCarrierNameFilter(filter?: string) {
    this.carrierNameFilter = filter
  }

  setFilterByVisitId(visitId?: number) {
    this.filterByVisitId = visitId
  }

  setFilterByRailCarTrackPositionId(railCarTrackPositionId?: number) {
    this.filterByRailcarTrackPositionId = railCarTrackPositionId
  }

  enableFilters(apply: boolean) {
    this.isFilterEnabled = apply
  }

  get unresolvedIssues(): IssueDto[] {
    return this.issueStore.items.filter(x => x.issueStatus !== IssueStatus.Resolved)
  }

  get dataFilteredByIssueStatus() {
    const data = this.filteredData ?? []

    return data.filter(
      i =>
        (!this.issueFilterStatus || i.issueStatus === this.issueFilterStatus) &&
        (!this.issueFilterType || i.issueType === this.issueFilterType),
    )
  }

  get issueTypes(): string[] {
    return _(this.filteredData)
      .map(i => i.issueType)
      .filter(i => !!i)
      .uniq()
      .value()
  }

  get filteredData(): IssueWithJourney[] {
    let filteredData = [...this.issues]

    // Filter by VisitId
    if (this.filterByVisitId) {
      filteredData = filteredData.filter(i => {
        const { containerJourneyData } = i

        return (
          containerJourneyData &&
          (containerJourneyData.inboundVisitId === this.filterByVisitId ||
            containerJourneyData.outboundVisitId === this.filterByVisitId)
        )
      })
    }

    // Filter by RailCarTrackPositionId
    if (this.filterByRailcarTrackPositionId) {
      filteredData = filteredData.filter(i => {
        const { containerJourneyData } = i

        return (
          containerJourneyData &&
          (containerJourneyData.inboundRailcarTrackPositionId ===
            this.filterByRailcarTrackPositionId ||
            containerJourneyData.outboundRailcarTrackPositionId ===
              this.filterByRailcarTrackPositionId)
        )
      })
    }

    // Filter by container number
    if (this.containerNumberFilter) {
      filteredData = filteredData.filter(i => {
        const { containerJourneyData } = i

        return (
          containerJourneyData &&
          containerJourneyData.containerNumber?.includes(this.containerNumberFilter!)
        )
      })
    }

    // Filter by carrier name
    if (this.carrierNameFilter && this.isFilterEnabled) {
      filteredData = filteredData.filter(i => {
        const { containerJourneyData } = i
        const filter = this.carrierNameFilter!.toLocaleUpperCase()

        return (
          containerJourneyData &&
          (containerJourneyData.inboundVisitIdentifier?.toLocaleUpperCase().includes(filter) ||
            containerJourneyData.outboundVisitIdentifier?.toLocaleUpperCase().includes(filter))
        )
      })
    }

    return filteredData
  }

  get issuesTypeCount() {
    const items = this.filteredData
    let unresolved = 0
    let followUp = 0

    items.forEach(item => {
      if (item.issueStatus === 'Unresolved') {
        unresolved++
      } else if (item.issueStatus === 'FollowUp') {
        followUp++
      }
    })

    return {
      all: items.length,
      unresolved: unresolved,
      followUp: followUp,
    }
  }

  clearFilters() {
    this.setCarrierNameFilter()
    this.setContainerNumberFilter()
    this.setFilterByVisitId()
    this.setFilterByRailCarTrackPositionId()
    this.enableFilters(false)
    this.clearChips()
  }

  clearChips() {
    this.setIssueFilterStatus()
    this.setIssueFilterType()
  }
}
